import { createContext, useContext, useRef, useCallback, useSyncExternalStore } from "react";
import { FEATURES_CONFIG } from "../featuresConfig";

// ─────────────────────────────────────────────────────────────
// SlotRegistryContext
//
// Centralized broker for PIXI container injection into spine
// bone slots. Providers (controllers) register named slots;
// consumers (React components) subscribe via useSlot() and
// re-render only when their specific slot becomes available.
//
// Uses useRef + subscription pattern (NOT useState) to avoid
// re-rendering the entire provider tree on each registration.
//
// Fully gated by FEATURES_CONFIG.SLOT_REGISTRY:
//   ENABLED: false → entire system is no-op (zero overhead)
//   Per-component toggles gate individual injection points
// ─────────────────────────────────────────────────────────────

const SlotRegistryContext = createContext(null);

// No-op stub returned by useSlotRegistry when master toggle is off.
// Controllers receive this and their registerSlot/unregisterSlot calls do nothing.
const NOOP_REGISTRY = Object.freeze({
  registerSlot: () => {},
  unregisterSlot: () => {},
  subscribe: () => () => {},
  getSlotSnapshot: () => NOOP_SNAPSHOT,
});

// Stable snapshot returned when registry is disabled — avoids allocating new objects.
const NOOP_SNAPSHOT = Object.freeze({ container: null });

// ─────────────────────────────────────────────────────────────
// Provider
// ─────────────────────────────────────────────────────────────

export const SlotRegistryProvider = ({ children }) => {
  // When master toggle is off, skip all internal state and provide no-op stub
  if (!FEATURES_CONFIG.SLOT_REGISTRY.ENABLED) {
    return (
      <SlotRegistryContext.Provider value={NOOP_REGISTRY}>
        {children}
      </SlotRegistryContext.Provider>
    );
  }

  return <SlotRegistryProviderActive>{children}</SlotRegistryProviderActive>;
};

// Active provider — only rendered when SLOT_REGISTRY.ENABLED is true
const SlotRegistryProviderActive = ({ children }) => {
  // Registry map: slotName → PIXI.Container
  const slotsRef = useRef(new Map());

  // Subscriber map: slotName → Set<callback>
  const subscribersRef = useRef(new Map());

  // Monotonic version counter — useSyncExternalStore requires a
  // getSnapshot return value that changes (by reference) when the
  // store mutates. We bump this on every register/unregister and
  // store per-slot snapshots so each useSlot hook only re-renders
  // when its own slot changes.
  const slotSnapshotsRef = useRef(new Map());

  // Notify all subscribers for a specific slot name
  const notifySlot = useCallback((name) => {
    const subs = subscribersRef.current.get(name);
    if (subs) {
      // Update the snapshot for this slot so useSyncExternalStore
      // detects a change (new object reference)
      slotSnapshotsRef.current.set(name, {
        container: slotsRef.current.get(name) ?? null,
      });
      subs.forEach((cb) => cb());
    }
  }, []);

  // Register a named slot with its PIXI.Container
  const registerSlot = useCallback(
    (name, container) => {
      slotsRef.current.set(name, container);
      // Create initial snapshot if none exists
      slotSnapshotsRef.current.set(name, { container });
      notifySlot(name);
    },
    [notifySlot],
  );

  // Unregister a named slot (cleanup)
  const unregisterSlot = useCallback(
    (name) => {
      slotsRef.current.delete(name);
      slotSnapshotsRef.current.set(name, { container: null });
      notifySlot(name);
    },
    [notifySlot],
  );

  // Subscribe to a specific slot — returns unsubscribe function
  const subscribe = useCallback((name, callback) => {
    if (!subscribersRef.current.has(name)) {
      subscribersRef.current.set(name, new Set());
    }
    subscribersRef.current.get(name).add(callback);

    return () => {
      const subs = subscribersRef.current.get(name);
      if (subs) {
        subs.delete(callback);
        if (subs.size === 0) {
          subscribersRef.current.delete(name);
        }
      }
    };
  }, []);

  // Get current snapshot for a slot (stable between mutations)
  const getSlotSnapshot = useCallback((name) => {
    if (!slotSnapshotsRef.current.has(name)) {
      slotSnapshotsRef.current.set(name, { container: null });
    }
    return slotSnapshotsRef.current.get(name);
  }, []);

  // Memoize the registry value to avoid re-creating on every render
  // This is safe because all functions are useCallback-stable
  const registryRef = useRef({
    registerSlot,
    unregisterSlot,
    subscribe,
    getSlotSnapshot,
  });

  return (
    <SlotRegistryContext.Provider value={registryRef.current}>
      {children}
    </SlotRegistryContext.Provider>
  );
};

// ─────────────────────────────────────────────────────────────
// useSlotRegistry — access raw registry (for passing to controllers)
//
// When master toggle is off, returns NOOP_REGISTRY — controllers
// receive no-op functions and their register calls silently do nothing.
// ─────────────────────────────────────────────────────────────

export const useSlotRegistry = () => {
  const ctx = useContext(SlotRegistryContext);
  if (!ctx) {
    throw new Error("useSlotRegistry must be used within SlotRegistryProvider");
  }
  return ctx;
};

// ─────────────────────────────────────────────────────────────
// useSlot — subscribe to a single named slot
//
// Returns PIXI.Container | null.
// Re-renders ONLY when the specific slot's container changes.
// Uses useSyncExternalStore for tear-free reads.
//
// When master toggle is off, always returns null (no subscription).
// ─────────────────────────────────────────────────────────────

export const useSlot = (name) => {
  const registry = useSlotRegistry();

  const container = useSyncExternalStore(
    // subscribe: register callback for this slot name
    (callback) => registry.subscribe(name, callback),
    // getSnapshot: return current snapshot object (reference-stable between changes)
    () => registry.getSlotSnapshot(name),
  );

  return container.container;
};

// ─────────────────────────────────────────────────────────────
// getCounterScale — centralized bone injection counter-scale
//
// When a container is injected into a spine bone slot, the spine's
// own scale is inherited. This function computes the inverse to
// cancel that inherited scale so injected content renders at 1:1.
//
// Returns 1 when slot registry or bone injection is disabled.
// ─────────────────────────────────────────────────────────────

export function getCounterScale(symbolScale) {
  if (!FEATURES_CONFIG.SLOT_REGISTRY.ENABLED) return 1;
  // Counter-scale needed for both legacy bone injection and GameView spine injection
  const needsCounterScale =
    FEATURES_CONFIG.BACKGROUND.REELSET_BONE_INJECTION ||
    FEATURES_CONFIG.GAME_VIEW?.ENABLED;
  if (!needsCounterScale) return 1;
  const spineScale = symbolScale;
  return spineScale > 0 ? 1 / spineScale : 1;
}

export default SlotRegistryContext;
